import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {apiV2} from "../../../api/axiosConfig";
import {CreateAPIKeyResponse} from "./types";
import {BlueButton} from "../../../components/ui/Buttons";
import {useAuth} from "../../../context/user";
import {ClientSelectSingle} from "../../../components/form-v2/searchable-select/ClientSelect";
import {Client} from "../../../types/userManagement";
import BasicText from "../../../components/form-v2/basic-text";
import {isAxiosError} from "axios";

const APIKeyCreate: React.FC = () => {
    const auth = useAuth();
    const navigate = useNavigate();

    const [name, setName] = useState("");
    const [expiresAt, setExpiresAt] = useState("");
    const [client, setClient] = useState<Client | undefined>();
    const [ed25519PublicKey, setEd25519PublicKey] = useState("");
    const [ed25519Enabled, setEd25519Enabled] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState("");
    const [createdKey, setCreatedKey] = useState<CreateAPIKeyResponse | null>(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if(!auth.developerAccess){
            navigate("/");
        }
    }, [auth.developerAccess]);

    const createAPIKey = async () => {
        if (!name.trim()) {
            setError("Name is required");
            return;
        }

        if (auth.isSuperAdmin && !client) {
            setError("Client is required");
            return;
        }

        setCreating(true);
        setError("");

        try {
            const payload: any = {
                name: name.trim(),
            };

            if (auth.isSuperAdmin && client?.id) {
                payload.clientID = client.id;
            }

            if (expiresAt) {
                // Convert to RFC3339 format
                const date = new Date(expiresAt);
                payload.expiresAt = date.toISOString();
            }

            if (ed25519PublicKey.trim()) {
                payload.ed25519PublicKey = ed25519PublicKey.trim();
                payload.ed25519Enabled = ed25519Enabled;
            }

            const response = await apiV2.post<CreateAPIKeyResponse>("client-api-keys", payload);
            setCreatedKey(response.data);
        } catch (e) {
            console.error(e);
            if (isAxiosError(e) && e?.response?.data?.error) {
                setError(e.response.data.error);
            } else {
                setError("Failed to create API key");
            }
        }
        setCreating(false);
    };

    const copyToClipboard = () => {
        if (createdKey?.apiKey) {
            navigator.clipboard.writeText(createdKey.apiKey);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    if (createdKey) {
        return (
                <div className="flex flex-col gap-8 w-full pt-4">
                    <div className="bg-white flex flex-col gap-4 rounded-md">
                        <h2 className="text-xl font-semibold text-green">API Key Created Successfully!</h2>

                        <div className="bg-light-orange p-4 rounded-md">
                            <p className="font-semibold text-dark-orange">Important: Save this API key now!</p>
                            <p className="text-sm text-dark-orange mt-2">
                                This is the only time you will see the full API key. Make sure to copy and store it securely.
                            </p>
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="font-semibold">Name:</label>
                            <p>{createdKey.name}</p>
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="font-semibold">API Key:</label>
                            <div className="bg-desaturated-grey p-3 rounded font-mono text-sm break-all">
                                {createdKey.apiKey}
                            </div>
                            <div>
                                <BlueButton
                                    text={copied ? "Copied!" : "Copy"}
                                    onClick={copyToClipboard}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="font-semibold">Expires:</label>
                            <p>{new Date(createdKey.expiresAt).toLocaleDateString()}</p>
                        </div>

                        {createdKey.ed25519Enabled && (
                            <div className="flex flex-col gap-2">
                                <label className="font-semibold">Ed25519 Signing:</label>
                                <p className="text-green">Enabled</p>
                            </div>
                        )}

                        <div className="flex gap-4 mt-4">
                            <BlueButton
                                text="Done"
                                onClick={() => navigate("/settings/api-keys")}
                            />
                        </div>
                    </div>
                </div>
        );
    }

    return (
            <div className="flex flex-col gap-8 w-full pt-4">
                <div className="bg-white flex flex-col gap-6 rounded-md">
                    <h2 className="text-xl font-semibold">Create New API Key</h2>

                    {error && (
                        <div className="text-white bg-red shadow-md p-4 rounded-md">
                            {error}
                        </div>
                    )}

                    {auth.isSuperAdmin && (
                        <div className="flex flex-col gap-2">
                            <label className="font-semibold">Client *</label>
                            <ClientSelectSingle
                                client={client}
                                onChange={setClient}
                            />
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <label className="font-semibold">Name *</label>
                        <BasicText
                            value={name}
                            onChange={setName}
                            placeholder="e.g., Production API Key, Mobile App Key"
                        />
                        <p className="text-sm text-grey">
                            A descriptive name to help you identify this key
                        </p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="font-semibold">Expires At (Optional)</label>
                        <input
                            type="date"
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                            className="border border-light-grey rounded p-2"
                        />
                        <p className="text-sm text-grey">
                            Leave empty to set expiration to 1 year from now
                        </p>
                    </div>

                    <div className="border-t border-light-grey pt-4">
                        <h3 className="font-semibold mb-4">Ed25519 Request Signing (Optional)</h3>

                        <div className="flex flex-col gap-2 mb-4">
                            <label className="font-semibold">Ed25519 Public Key</label>
                            <textarea
                                value={ed25519PublicKey}
                                onChange={(e) => setEd25519PublicKey(e.target.value)}
                                placeholder="Paste your base64-encoded Ed25519 public key (32 bytes)"
                                className="border border-light-grey rounded p-2 font-mono text-sm"
                                rows={3}
                            />
                            <p className="text-sm text-grey">
                                Generate a keypair locally and paste the public key here. The private key should never be shared.
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="ed25519Enabled"
                                checked={ed25519Enabled}
                                onChange={(e) => setEd25519Enabled(e.target.checked)}
                                className="w-4 h-4"
                            />
                            <label htmlFor="ed25519Enabled" className="text-sm">
                                Enable Ed25519 signature validation for this API key
                            </label>
                        </div>
                    </div>

                    <div className="flex gap-4 mt-4">
                        <BlueButton
                            text="Create API Key"
                            loading={creating}
                            onClick={createAPIKey}
                        />
                        <button
                            onClick={() => navigate("/api-keys")}
                            className="px-4 py-2 border border-light-grey rounded hover:bg-desaturated-grey"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
    );
};

export default APIKeyCreate;
